/* hero-section-accordion.js
 *
 * Fixed-height variant of `section-hero`: when the merchant configures
 * a fixed section height AND places an accordion inside, the inner
 * `.hero-accordion` sits at `bottom: 0` of a JS-sized container
 * (see accordion.js `setHeroAccordionContainerHeight`) as an overlay
 * that expands upward over content. On short viewports an expanded
 * item can overflow the section's top edge and get clipped, leaving
 * the label (toggle target) unreachable — hence this module.
 *
 * Behaviour:
 *   For each `.hero-section--fixed` on the page, watch its inner
 *   `.hero-accordion` for size changes (expand / collapse animation
 *   from jQuery slideUp / slideDown mutates offsetHeight over many
 *   frames — ResizeObserver picks that up automatically). When any
 *   accordion item is `.active`, compute the section's required
 *   `min-height` as:
 *
 *     accordion.offsetHeight + slide.padding-top + slide.padding-bottom
 *
 *   and write it to the `--hero-required-min-height` CSS variable on
 *   the section. The section's inline `min-height` (set in Liquid)
 *   uses `max()` against this variable, so the section grows to fit
 *   the accordion when it exceeds the fixed value and collapses back
 *   to the fixed floor when the accordion closes. Overlay behaviour
 *   is preserved — the accordion stays absolute-positioned inside
 *   its container.
 *
 * Scope guarantees:
 *   * Only affects `.hero-section--fixed`. Other hero variants
 *     (`--full`, etc.) and unrelated accordions elsewhere on the
 *     page are untouched.
 *   * Every section carries its own `ResizeObserver` + its own CSS
 *     variable, so multiple hero sections on one page (unusual but
 *     supported) don't share state or interfere with each other.
 *   * Idempotent init — a section that's already been wired carries
 *     a `data-hero-accordion-min-height-init` marker; running init()
 *     again (e.g. if the script tag is emitted twice on a page with
 *     multiple hero sections) is a no-op.
 *   * ResizeObserver check with a fallback to a `MutationObserver`
 *     watching `.accordion-item.active` for the sub-percent of
 *     browsers still without RO support.
 */

(function () {
  'use strict';

  var CSS_VAR = '--hero-required-min-height';
  var INIT_ATTR = 'data-hero-accordion-min-height-init';
  var SECTION_SELECTOR = '.hero-section--fixed';
  var ACCORDION_SELECTOR = '.hero-accordion';
  var SLIDE_SELECTOR = '.hero-section__slide';
  var ACTIVE_ITEM_SELECTOR = '.accordion-item.active';

  function measureAndUpdate(section, accordion, slide) {
    // No expanded items → clear the variable so the CSS floor (the
    // fixed height from section settings) takes over. The `max()` in
    // Liquid's inline style resolves to just the fixed value.
    if (!section.querySelector(ACTIVE_ITEM_SELECTOR)) {
      section.style.removeProperty(CSS_VAR);
      return;
    }

    // Read the slide's actual block padding so the accordion's top
    // edge respects the section's block-padding setting when the
    // section grows. Slide padding-top may include the navbar height
    // (see snippets/block-padding.liquid `include_navbar` branch);
    // that's fine — we want to preserve whatever the merchant
    // configured.
    var slideStyles = window.getComputedStyle(slide);
    var paddingTop = parseFloat(slideStyles.paddingTop) || 0;
    var paddingBottom = parseFloat(slideStyles.paddingBottom) || 0;

    var accordionHeight = accordion.offsetHeight;
    var required = accordionHeight + paddingTop + paddingBottom;

    section.style.setProperty(CSS_VAR, required + 'px');
  }

  function initSection(section) {
    if (section.hasAttribute(INIT_ATTR)) return;
    section.setAttribute(INIT_ATTR, 'true');

    var accordion = section.querySelector(ACCORDION_SELECTOR);
    var slide = section.querySelector(SLIDE_SELECTOR);
    if (!accordion || !slide) return;

    var update = function () {
      measureAndUpdate(section, accordion, slide);
    };

    if (typeof ResizeObserver !== 'undefined') {
      // ResizeObserver fires on every layout change of the accordion
      // element — including the many intermediate frames of jQuery's
      // slideDown / slideUp animation — so the section's min-height
      // tracks the accordion's growth smoothly through the animation.
      var observer = new ResizeObserver(update);
      observer.observe(accordion);
    } else {
      // Fallback for browsers without ResizeObserver: watch the
      // .active class on accordion items. Only fires at the two
      // endpoints (open / close), not during the animation, so the
      // section snaps to its final size after the animation
      // completes. Good enough for the safety net.
      var mo = new MutationObserver(update);
      mo.observe(accordion, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
      });
    }

    // Viewport resize can change padding-top / padding-bottom on the
    // slide (breakpoints in snippets/block-padding.liquid) and the
    // fixed-height floor (which is set per breakpoint in
    // section-hero.liquid), so recompute on window resize. Debounced
    // via a small timeout so a resize drag doesn't fire hundreds of
    // updates a second.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(update, 100);
    });

    // Initial measurement — if a page reload preserves an active
    // accordion item (rare here — accordion.js clears .active on
    // its container-height setup), this catches it. Otherwise this
    // is a no-op (early return above).
    update();
  }

  function init() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(function (section) {
      initSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
